import { foldText } from "@/lib/foldText";
import { SKILL_TAXONOMY, skillKey } from "@/lib/skills";

/**
 * Work domains for the profile track record. They are broader than
 * `SKILL_TAXONOMY`, which is the skill picker's office-and-screen vocabulary.
 * Most real task skills are free text (`local-errands`, `limpieza`, `plumbing`,
 * `guarani`). Matched against the taxonomy alone, nearly all of them fell into
 * "Other", so a handyman's radar looked like an empty office job.
 *
 * The display order goes around the radar from screen work to people work to
 * hands-on work, so a profile's shape shows where its work actually happens.
 */
export const SKILL_DOMAINS = [
  "tech",
  "business",
  "marketing",
  "language",
  "creative",
  "community",
  "care",
  "local",
  "trades",
] as const;

export type SkillDomain = (typeof SKILL_DOMAINS)[number];
/** Skills nothing recognises. Listed, but never drawn as a radar axis. */
export const OTHER_DOMAIN = "other";
export type DomainOrOther = SkillDomain | typeof OTHER_DOMAIN;

const DOMAIN_BY_CATEGORY: Record<string, SkillDomain> = {
  Development: "tech",
  Data: "tech",
  Design: "creative",
  Writing: "language",
  Research: "business",
  Marketing: "marketing",
  Learning: "community",
  Languages: "language",
  Home: "care",
  Care: "care",
  Local: "local",
  Music: "creative",
  Sports: "community",
  Events: "care",
};

/** Taxonomy terms whose category is the wrong domain, or that sit in "Other". */
const TAXONOMY_OVERRIDES: Record<string, SkillDomain> = {
  "Music Practice": "creative",
  "Language Learning": "language",
  "Customer Support": "care",
  "Virtual Assistant": "business",
  Transcription: "language",
  Testing: "tech",
  Photography: "creative",
  "Audio Production": "creative",
  Gardening: "local",
  Handyman: "trades",
  Repairs: "trades",
  Sewing: "creative",
  "Music Teaching": "community",
};

const EXACT: ReadonlyMap<string, SkillDomain> = new Map([
  ...Object.entries(SKILL_TAXONOMY).flatMap(([category, skills]) =>
    skills.flatMap((s) => {
      const d = TAXONOMY_OVERRIDES[s] ?? DOMAIN_BY_CATEGORY[category];
      return d ? [[skillKey(s), d] as const] : [];
    }),
  ),
  // Free-text values whose keywords would point at the wrong domain.
  ["design-thinking", "business"],
]);

/**
 * Keyword stems in English, Spanish and Portuguese. Each one matches at the
 * start of a word, so `plumb` matches "plumbing" and "plumber", and
 * `repar` matches "reparación". A trailing `$` means the whole word only,
 * so `it$` matches "it support" but not "italian".
 *
 * ORDER IS PRIORITY. A skill goes to the first domain it matches, and the order
 * resolves the collisions that show up in real skills:
 *   - trades before care, so "drain cleaning" is a repair
 *   - local before care, so "event logistics" is logistics
 *   - creative before language, so "video editing" is media
 *   - language before marketing, so "content writing" is writing
 *   - marketing before community, so "community manager" is marketing
 *   - business before tech, so "web research" is research
 *
 * Tech sits last, so its broad stems ("web", "data", "dev") can't take
 * "web design" or "community development". That leaves tech skills built on
 * generic words ("bug fixing", "unit testing", "data analytics") to be caught
 * by an earlier domain's "fix", "testing" or "analy". `TECH_ANCHORS` runs
 * before every domain and holds only phrases that are tech whatever else
 * they contain.
 */
const TECH_ANCHORS: string[] = [
  "bug$", "bugs$", "software", "server", "programming", "programac", "programmer", "developer",
  "clean code", "unit test", "integration test", "software test", "qa test", "load test",
  "performance test", "test automat", "data analy", "web analy", "google analy", "sql",
  "database", "api$", "apis$", "website maint", "web maint", "site maint", "system maint",
  "system admin", "sysadmin", "windows server", "device driver", "firmware", "neural",
  "prompt engineer", "model train", "ai train",
];

const DOMAIN_KEYWORDS: [SkillDomain, string[]][] = [
  [
    "trades",
    [
      "plumb", "plomer", "fontaner", "encanad", "gasfit", "carpent", "carpint", "marcen", "electric", "eletric",
      "handyman", "repair", "repara", "reparo", "fix", "maint", "manten", "manuten", "glaz", "vidri", "window$",
      "ventana", "janela", "faucet", "grifo", "torneira", "drain", "desag", "lock", "cerraj",
      "chaveir", "weld", "sold", "mason", "alban", "pedreir", "construct", "constru", "roof",
      "techad", "hvac", "air condition", "aire acond", "mechanic", "mecanic", "automotive",
      "automot", "appliance", "electrodom", "eletrodom", "furniture", "mueble", "movel", "moveis",
      "assembl", "ensambl", "montaj", "tiling", "tile", "azulej", "house paint", "renovat",
      "remodel", "reform", "demolit", "demolic", "motor$", "motos$", "detailing",
    ],
  ],
  [
    "local",
    [
      "errand", "mandado", "recado", "tramite", "delivery", "deliver", "entreg", "repart",
      "courier", "mensajer", "driving", "driver", "conduc", "chofer", "motorist", "logistic", "moving",
      "mudanz", "desalojo", "physical", "labor$", "labour$", "laborer", "warehouse", "deposit", "pickup",
      "shopping", "compras", "local$", "locales", "travel", "viaj", "viag", "tour", "guid",
      "guia", "transport", "farm", "agro", "garden", "jardin", "landscap", "outdoor", "cultur",
      "histor",
    ],
  ],
  [
    "care",
    [
      "clean", "limpi", "limpe", "lavand", "laundry", "cater", "food", "comida", "cook", "cocin",
      "cozinh", "chef", "bak", "panad", "confeit", "hospital", "hotel", "airbnb", "event",
      "childcare", "child", "ninos", "crianc", "babysit", "nanny", "niñera", "elder", "ancian",
      "idos", "pet$", "pets$", "dog", "perro", "nurs", "enfermer", "medic", "salud", "saude",
      "health", "neuro", "therap", "terap", "wine", "vino", "sommel", "bartend", "waiter", "mesero",
      "garcom", "customer serv", "customer care", "atencion", "atendim", "reception", "recepcion", "concierge",
      "family", "familia", "beauty", "belleza", "beleza", "hair", "peluq", "cabel", "makeup",
      "maquill", "maquiag", "massage", "masaj", "massag", "caregiv", "cuidad", "lavado", "animal", "reserv",
    ],
  ],
  [
    "creative",
    [
      "photo", "foto", "video", "film", "filmac", "camera", "design", "diseno", "illustr",
      "ilustr", "drawing", "draw", "dibuj", "desenh", "animat", "animac", "3d$", "music", "musica",
      "sing", "canto$", "cantar", "cantor", "cantant", "guitar", "piano", "audio", "sound", "sonido", "som$", "recording", "grabac",
      "gravac", "podcast", "art$", "arts$", "arte$", "artes$", "artist", "craft", "artesan", "paint",
      "pintur", "figma", "canva$", "logo", "ui$", "ux$", "wirefram", "lighting", "iluminac",
      "portrait", "retrat", "audiovisual", "entertain", "entreten", "payaso", "palhac", "clown",
      "danc", "danza", "theat", "teatr", "acting", "actor", "print", "impres",
      "calligraph", "caligraf", "shufa", "handwriting", "typograph", "tipograf", "color theory",
      "visual ident", "identidad visual", "adobe", "post production", "infographic", "creativ",
      "globoflex", "balloon", "ugc$", "moda$", "fashion design",
    ],
  ],
  [
    "language",
    [
      "writ", "escrit", "escrev", "redac", "redat", "copy", "translat", "traduc", "traduç", "transcri",
      "proofread", "edit", "blog", "script$", "scripts$", "screenwrit", "subtit", "legend", "spanish", "espanol", "english",
      "ingles", "guarani", "portugu", "french", "frances", "german", "aleman", "alema", "italian",
      "japanese", "japones", "mandarin", "chinese", "chino", "chines", "korean", "arabic",
      "linguist", "multilingu", "bilingu", "language", "idioma", "lengua", "lingua", "localiz",
      "periodis", "journalis",
    ],
  ],
  [
    "marketing",
    [
      "market", "mercad", "seo", "sem$", "ads$", "advert", "publicid", "social media", "redes",
      "tiktok", "instagram", "facebook", "meta ads", "influenc", "growth", "brand", "marca",
      "sales", "venta", "vend", "content", "contenid", "conteud", "campaign", "campan", "newsletter",
      "conversion", "saas", "pr$", "promot", "promoc", "lead$", "leads$", "lead gen", "crm$", "ecommerce", "commerce",
      "public relat", "relaciones public", "relacoes public", "cold email", "linkedin outreach", "product hunt",
      "aso$", "meta tag", "schema markup",
      "community manag",
    ],
  ],
  [
    "community",
    [
      "volunt", "community", "comunidad", "comunidade", "comunitar", "social", "ngo$", "ong$",
      "nonprofit", "charity", "caridad", "tutor", "teach", "ensen", "ensin", "docen", "profesor",
      "professor", "mentor", "educa", "coach", "facilit", "course", "curso", "lesson", "clase",
      "aula", "training", "capacit", "onboard", "sport", "deport", "esport", "football", "futbol",
      "soccer", "communicat", "comunicac", "teamwork", "trabajo en equipo", "trabalho em equipe",
      "team", "public speak", "oratoria", "networking", "present", "listening",
    ],
  ],
  [
    "business",
    [
      "research", "investig", "pesquis", "analy", "analis", "survey", "encuest", "strateg",
      "estrateg", "plan$", "planning", "planif", "planeac", "planej", "project", "proyect", "projet", "consult", "legal", "law$", "lawyer",
      "derecho", "direito", "abog", "advog", "arrend", "contrat", "notar", "account", "contab",
      "financ", "tax$", "impuest", "admin", "virtual assist", "asistent", "assistent", "secretar",
      "organiz", "gestion", "gestao", "manag", "business", "negocio", "entrepren", "emprend",
      "problem solv", "critical think", "feedback", "testing", "user test", "qa$", "quality",
      "report", "compet", "benchmark",
      "customer analy", "negotiat", "negocia", "comercio exterior", "import", "export", "aduan",
      "customs", "currency", "incorporat", "government", "legislac", "compliance", "scheduling",
      "documentat", "interview", "entrevist", "recruit", "reclut", "product", "swot$",
    ],
  ],
  [
    "tech",
    [
      "dev", "desarroll", "desenvolv", "program", "code", "coding", "software", "javascript",
      "typescript", "python", "java", "rust", "solidity", "react", "node", "js$", "html", "css",
      "web", "frontend", "front end", "backend", "back end", "full stack", "fullstack", "api$",
      "sql", "data", "dato", "dado", "database", "machine learning", "ml$", "ai$", "ia$",
      "inteligencia artificial", "artificial intell", "hardware", "router", "tech", "tecnolog",
      "it$", "computer", "computac", "informat", "electronic", "eletronic", "no code",
      "nocode", "blockchain", "web3", "smart contract", "crypto", "spreadsheet", "excel",
      "etl$", "automat", "deploy", "cloud", "troubleshoot", "big data",
      "docker", "kubernet", "terraform", "ansible", "linux", "bash$", "aws$", "azure", "gcp$",
      "ci cd", "devops", "zapier", "adalo", "spark$", "hadoop", "nlp$", "security", "cyber",
      "domain setup", "accessib", "perform", "optimiz", "form handl",
    ],
  ],
];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const PRIORITY: [SkillDomain, string[]][] = [["tech", TECH_ANCHORS], ...DOMAIN_KEYWORDS];

const DOMAIN_PATTERNS: [SkillDomain, RegExp][] = PRIORITY.map(([domain, stems]) => {
  const alts = stems.map((kw) => {
    const whole = kw.endsWith("$");
    const stem = escapeRegex(foldText(whole ? kw.slice(0, -1) : kw));
    return whole ? `${stem}(?= |$)` : stem;
  });
  return [domain, new RegExp(`(?:^| )(?:${alts.join("|")})`)];
});

/** Words, lower-cased and accent-free: "Diseño gráfico" → "diseno grafico". */
function words(skill: string): string {
  return foldText(skill)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .join(" ");
}

/** The domain a stored skill belongs to: an exact taxonomy match first, then keywords. */
export function domainOf(skill: string): DomainOrOther {
  const exact = EXACT.get(skillKey(skill));
  if (exact) return exact;
  const w = words(skill);
  if (!w) return OTHER_DOMAIN;
  for (const [domain, re] of DOMAIN_PATTERNS) {
    if (re.test(w)) return domain;
  }
  return OTHER_DOMAIN;
}
